import { Box, Chip, CircularProgress } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { downloadChatFile } from '../../utils/axios/api';
import { MessageFileChipProps } from './message-file-chip.interface';

const IMAGE_EXTENSION_RE = /\.(avif|bmp|gif|jpe?g|png|svg|webp)$/i;

function isImageFile(mimeType?: string, name?: string) {
  if (mimeType?.startsWith('image/')) {
    return true;
  }

  return IMAGE_EXTENSION_RE.test(name ?? '');
}

export function MessageFileChipComponent({
  id,
  name,
  mimeType,
  isUploading = false,
  downloadFile = downloadChatFile,
}: MessageFileChipProps) {
  const isImage = useMemo(
    () => isImageFile(mimeType, name),
    [mimeType, name],
  );
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [isThumbnailLoading, setIsThumbnailLoading] = useState(false);

  useEffect(() => {
    if (!isImage || isUploading) {
      setThumbnailUrl(null);
      setIsThumbnailLoading(false);
      return;
    }

    let objectUrl: string | undefined;
    let cancelled = false;

    setIsThumbnailLoading(true);

    void downloadFile(id)
      .then((blob) => {
        const nextUrl = URL.createObjectURL(blob);

        if (cancelled) {
          URL.revokeObjectURL(nextUrl);
          return;
        }

        objectUrl = nextUrl;
        setThumbnailUrl(nextUrl);
      })
      .catch(() => {
        if (!cancelled) {
          setThumbnailUrl(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsThumbnailLoading(false);
        }
      });

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [downloadFile, id, isImage, isUploading]);

  if (isImage) {
    return (
      <Box
        sx={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          borderRadius: 1.5,
          border: 1,
          borderColor: 'divider',
          bgcolor: 'grey.50',
          minWidth: 72,
          minHeight: 72,
          maxWidth: 160,
          maxHeight: 160,
        }}
        title={name}
      >
        {thumbnailUrl ? (
          <Box
            component="img"
            src={thumbnailUrl}
            alt={name}
            sx={{
              display: 'block',
              maxWidth: 160,
              maxHeight: 160,
              objectFit: 'cover',
            }}
          />
        ) : null}
        {isUploading || isThumbnailLoading ? (
          <CircularProgress
            size={18}
            sx={{
              position: thumbnailUrl ? 'absolute' : 'static',
            }}
          />
        ) : null}
        {!thumbnailUrl && !isUploading && !isThumbnailLoading ? (
          <Chip label={name} size="small" sx={{ m: 0.75 }} />
        ) : null}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Chip label={name} size="small" />
      {isUploading ? <CircularProgress size={14} /> : null}
    </Box>
  );
}
